import React, { useState } from 'react';
import './InteractiveDemo.css';
import { Sparkles, Copy, Check } from 'lucide-react';

function InteractiveDemo() {
  const [copied, setCopied] = useState(null);

  const sampleOutputs = {
    linkedin: [
      {
        id: 1,
        type: 'LinkedIn Post #1',
        text: "🚀 After analyzing 50+ newsletter creators, I discovered the #1 reason most fail to grow:\n\nThey write great content... but nobody sees it.\n\nHere's what changed everything for me:\n\n👇 Thread on distribution strategy",
        hook: 'Pattern interrupt',
      },
      {
        id: 2,
        type: 'LinkedIn Post #2',
        text: "Most newsletter creators spend 3-4 hours weekly on repurposing.\n\nI automated mine in 90 seconds.\n\nHere's the exact system I use:\n\n[Swipe this framework]",
        hook: 'Specific promise',
      },
    ],
    carousel: [
      {
        id: 1,
        type: 'LinkedIn Carousel',
        title: 'Slide 1: The Problem',
        text: '74% of creators spend 3-4 hours weekly repurposing content',
        design: 'Stat-driven opener',
      },
      {
        id: 2,
        type: 'LinkedIn Carousel',
        title: 'Slide 2: The Solution',
        text: 'AI-powered repurposing cuts this to 90 seconds—automated & platform-ready',
        design: 'Solution reveal',
      },
    ],
    twitter: [
      {
        id: 1,
        type: 'Standalone Tweet #1',
        text: 'The biggest lie in newsletter growth:\n\n"Just write good content"\n\nWrong.\n\nDistribution > Creation\n\nHere\'s proof: 🧵',
        hook: 'Contrarian take',
      },
      {
        id: 2,
        type: 'Standalone Tweet #2',
        text: 'Newsletter growth hack nobody talks about:\n\n1. Write once\n2. Repurpose 6x\n3. Post everywhere\n4. Watch subscribers grow\n\nRepeat weekly.',
        hook: 'Framework',
      },
      {
        id: 3,
        type: 'Standalone Tweet #3',
        text: "Stop losing 3-4 hours weekly to manual repurposing.\n\nAI can do it in 90 seconds.\n\nHere's how:",
        hook: 'Quick win',
      },
      {
        id: 4,
        type: 'Twitter Thread',
        text: 'Tweet 1/5: Your newsletter deserves more readers.\n\nBut 95% of creators make this fatal mistake...\n\nTweet 2/5: They spend hours writing but zero time distributing.\n\n[Full thread continues...]',
        hook: 'Story-driven thread',
      },
    ],
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <section className='interactive-demo' id='demo'>
      <div className='demo-container'>
        <div className='demo-header'>
          <div className='demo-badge'>
            <Sparkles size={16} />
            <span>Live Example</span>
          </div>
          <h2 className='demo-title'>Exactly What You Get</h2>
          <p className='demo-subtitle'>
            From one newsletter, you get 6+ platform-specific variations: 2
            LinkedIn posts, 1 carousel, 3 tweets, and 1 thread
          </p>
        </div>

        {/* LinkedIn Posts */}
        <div className='output-section'>
          <h3 className='output-title'>
            <span className='platform-icon'>💼</span>
            LinkedIn Posts (2 variations)
          </h3>
          <div className='output-grid'>
            {sampleOutputs.linkedin.map((post) => (
              <div key={post.id} className='output-card'>
                <div className='output-header'>
                  <span className='output-type'>{post.type}</span>
                </div>
                <div className='output-content'>
                  <p>{post.text}</p>
                </div>
                <div className='output-footer'>
                  <span className='output-tag'>{post.hook}</span>
                  <button
                    className='copy-btn'
                    onClick={() => copyToClipboard(post.text, `li-${post.id}`)}
                  >
                    {copied === `li-${post.id}` ? (
                      <>
                        <Check size={16} />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy size={16} />
                        Copy
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* LinkedIn Carousel */}
        <div className='output-section'>
          <h3 className='output-title'>
            <span className='platform-icon'>📊</span>
            LinkedIn Carousel (1 full deck)
          </h3>
          <div className='output-grid'>
            {sampleOutputs.carousel.map((slide) => (
              <div key={slide.id} className='output-card carousel'>
                <div className='output-header'>
                  <span className='output-type'>{slide.type}</span>
                </div>
                <div className='carousel-preview'>
                  <strong>{slide.title}</strong>
                  <p>{slide.text}</p>
                </div>
                <div className='output-footer'>
                  <span className='output-tag'>{slide.design}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Twitter Content */}
        <div className='output-section'>
          <h3 className='output-title'>
            <span className='platform-icon'>🐦</span>
            Twitter (3 standalone tweets + 1 thread)
          </h3>
          <div className='output-grid'>
            {sampleOutputs.twitter.map((tweet) => (
              <div key={tweet.id} className='output-card'>
                <div className='output-header'>
                  <span className='output-type'>{tweet.type}</span>
                </div>
                <div className='output-content'>
                  <p>{tweet.text}</p>
                </div>
                <div className='output-footer'>
                  <span className='output-tag'>{tweet.hook}</span>
                  <button
                    className='copy-btn'
                    onClick={() =>
                      copyToClipboard(tweet.text, `tw-${tweet.id}`)
                    }
                  >
                    {copied === `tw-${tweet.id}` ? (
                      <>
                        <Check size={16} />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy size={16} />
                        Copy
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className='demo-cta'>
          <button
            className='btn-primary'
            onClick={() => {
              const element = document.getElementById('signup');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            Apply for Beta Access
            <Sparkles size={20} />
          </button>
          <p className='demo-note'>
            Limited to 25 beta users • Lifetime free access
          </p>
        </div>
      </div>
    </section>
  );
}

export default InteractiveDemo;
